/**
 * M-06 «Добавить контакт» (изменение v1.6) — как «New Contact» в Telegram.
 *
 * Человека ищут по номеру телефона: номер нормализуется в E.164 той же маской, что и вход,
 * и уходит в `POST /contacts/lookup`. Ответ всегда одинаково нейтральный — «никого с таким
 * номером»: по нему нельзя отличить «не зарегистрирован» от «не хочет, чтобы его находили»
 * (BS §11.5). Найденную карточку можно добавить в контакты и сразу написать.
 */
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { useContactActions } from '@features/contacts/use-contacts'
import { api, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { UserCard } from '@shared/api/types'
import { EMPTY_PHONE, phoneProblem, toE164, type PhoneValue } from '@shared/lib/phone'
import { Avatar, Button, EmptyState, Field, PhoneInput, useToast } from '@shared/ui'

import { Screen } from '../../layouts/Screen'
import styles from './contacts.module.css'

export function AddContactScreen(): React.ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const { add, busy } = useContactActions()

  const [phone, setPhone] = useState<PhoneValue>(EMPTY_PHONE)
  const [searching, setSearching] = useState(false)
  const [found, setFound] = useState<UserCard | null>(null)
  const [missing, setMissing] = useState(false)

  const e164 = toE164(phone)
  const problem = phone.digits.length ? phoneProblem(phone) : null

  const search = async (): Promise<void> => {
    if (!e164) return
    setSearching(true)
    setFound(null)
    setMissing(false)
    try {
      const result = unwrap(await api.POST('/contacts/lookup', { body: { phone: e164 } as never }))
      setFound(result.user)
    } catch (error) {
      if (error instanceof ApiError && error.code === 'not_found') {
        setMissing(true)
      } else if (error instanceof ApiError && error.code === 'rate_limited') {
        toast.show('Слишком много запросов — попробуйте позже', { tone: 'danger' })
      } else {
        toast.show('Не удалось выполнить поиск', { tone: 'danger' })
      }
    } finally {
      setSearching(false)
    }
  }

  const addAndOpen = async (): Promise<void> => {
    if (!found) return
    if (await add(found.id)) {
      void navigate({ to: '/users/$userId', params: { userId: found.id } })
    }
  }

  return (
    <Screen title="Добавить контакт" back narrow>
      <p className={styles['addLead']}>
        Найдите человека по номеру телефона — так же, как в записной книжке.
      </p>

      <Field label="Номер телефона" error={problem ?? undefined}>
        {({ id, invalid }) => (
          <PhoneInput
            id={id}
            value={phone}
            onValueChange={(next) => {
              setPhone(next)
              setFound(null)
              setMissing(false)
            }}
            invalid={invalid}
            autoFocus
            onKeyDown={(event) => {
              if (event.key === 'Enter' && e164) void search()
            }}
          />
        )}
      </Field>

      <Button variant="primary" fullWidth loading={searching} disabled={!e164} onClick={() => void search()}>
        Найти
      </Button>

      {missing ? (
        <EmptyState
          icon="search"
          title="Никого с таким номером"
          text="Проверьте номер. Человек мог не зарегистрироваться или закрыть поиск по телефону в настройках."
        />
      ) : null}

      {found ? (
        <div className={styles['foundCard']}>
          <Avatar name={found.display_name} photo={found.photos[0] ?? null} size="xl" />
          <div className={styles['foundText']}>
            <span className={styles['foundName']}>
              {found.display_name ?? 'Без имени'}
              {found.age !== null ? `, ${found.age}` : ''}
            </span>
            {found.username ? <span className={styles['foundHandle']}>@{found.username}</span> : null}
            {found.city ? <span className={styles['foundCity']}>{found.city.name}</span> : null}
          </div>
          <div className={styles['foundActions']}>
            {found.is_contact ? (
              <Button
                variant="secondary"
                fullWidth
                onClick={() => void navigate({ to: '/users/$userId', params: { userId: found.id } })}
              >
                Уже в контактах — открыть
              </Button>
            ) : (
              <Button variant="primary" fullWidth loading={busy} onClick={() => void addAndOpen()}>
                Добавить в контакты
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </Screen>
  )
}
